import React from 'react'
import styled from '@emotion/styled'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, Icon } from '@mobi/component-library/Common/V2'
import { colors, radius, shadow, spacing } from '@mobi/component-library/Theme/Common'
import { addToast, ToastContent } from '@mobi/component-library/Feedback/Toast'
import { Input } from '@mobi/component-library/Common/V2/Inputs/Input'
import { TextArea } from '@mobi/component-library/Common/V2/Inputs/TextArea'
import { LoadingPlaceholder } from '@mobi/component-library/Common/LoadingPlaceholder'
import { Flex } from '@mobi/component-library/Common/Flex'
import { queryClient } from '@core/Data/ReactQuery'
import { putFollowMeProfileDetails } from '@core/Areas/FollowMe/helpers/api'
import { followMePrincipleProfileQuery } from '@core/Areas/FollowMe/helpers/queries'
import { ProfileAvatar } from '@core/Areas/FollowMe/Components/Common/ProfileAvatar'
import type { FollowMeProfile } from '@core/Areas/FollowMe/types'
import { trackFollowMeProfileUpdated } from '@core/Areas/FollowMe/helpers/analytics'
import { EditAvatar } from './Components/EditAvatar'
import { ApiServiceError } from '@mobi/account/Utils'
import { queryKeys } from '@core/Data/ReactQuery/constants'

type Props = {
  onSuccessCallback: () => void
  onEditAvatarClick: () => void
  isEditingProfile: boolean
}

export const EditMyProfile: React.FC<Props> = ({
  onSuccessCallback,
  onEditAvatarClick,
  isEditingProfile,
}) => {
  const { isLoading, data } = useQuery(followMePrincipleProfileQuery)

  if (isLoading || !data) {
    return (
      <Flex flexDirection='column' gap={spacing.big}>
        <Flex justifyContent='center'>
          <LoadingPlaceholder width='12rem' height='12rem' borderRadius={radius.full} />
        </Flex>

        <LoadingPlaceholder height='9.8rem' borderRadius={radius.lgx1} />
        <LoadingPlaceholder height='19rem' borderRadius={radius.lgx1} />
        <LoadingPlaceholder height='4.4rem' borderRadius={radius.lgx1} />
      </Flex>
    )
  }

  return (
    <EditMyProfileComponent
      onEditAvatarClick={onEditAvatarClick}
      onSuccessCallback={onSuccessCallback}
      isEditingProfile={isEditingProfile}
      myProfile={data}
    />
  )
}

// ===============
// Local Component
// ===============

const EditMyProfileComponent: React.FC<Props & { myProfile: FollowMeProfile }> = ({
  onEditAvatarClick,
  onSuccessCallback,
  isEditingProfile,
  myProfile,
}) => {
  const [username, setUsername] = React.useState<string>(myProfile.displayName)
  const [bio, setBio] = React.useState<string>(myProfile.description || '')

  const [usernameError, setUsernameError] = React.useState('')
  const [bioError, setBioError] = React.useState('')

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      putFollowMeProfileDetails({
        displayName: username,
        description: bio,
      }),

    onSuccess: () => {
      onSuccessCallback()
      queryClient.invalidateQueries(followMePrincipleProfileQuery)
      addToast({
        id: 'profile-edit-success',
        content: <ToastContent title='Profile updated successfully.' icon='success' />,
      })

      trackFollowMeProfileUpdated()
      queryClient.invalidateQueries({
        queryKey: queryKeys.followMeAccountProfile,
      })
    },

    onError: async (err: Error) => {
      if (isError(err)) {
        const error = await err.response.json()

        if (error.detail?.includes('bio')) {
          setBioError(error.detail)
        } else {
          setUsernameError(error.detail)
        }
      }

      addToast({
        id: 'profile-edit-error',
        content: <ToastContent title='Failed to edit profile' icon='error' />,
      })
    },
  })

  const shouldAllowSave = myProfile.displayName !== username || myProfile.description !== bio

  const hasError = Boolean(usernameError || bioError)

  const handleChangeUserName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    if (usernameError) setUsernameError('')

    if (value.length <= 25) {
      setUsername(value)
    }
  }

  const handleBioChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (bioError) setBioError('')

    setBio(e.target.value)
  }

  const handleProfileSubmit = () => {
    const { valid, errors } = validateInputs(username, bio)

    setUsernameError(errors.username || '')
    setBioError(errors.bio || '')

    if (valid) {
      mutate()
    }
  }

  if (isEditingProfile) {
    return (
      <EditProfileContainerStyled>
        <button type='button' onClick={onEditAvatarClick}>
          <ProfileAvatar avatar={myProfile.avatar} color={myProfile.color} size='lg' />
          <span>
            <Icon name='LinePencil01' size='1.6rem' />
          </span>
        </button>

        <Input
          label='Username'
          name='Username'
          type='text'
          placeholder='Your username'
          value={username}
          helpText={
            usernameError ? usernameError : 'Your username is visible to other TABtouch users'
          }
          isInvalid={!!usernameError}
          onChange={handleChangeUserName}
        />

        <TextArea
          name='Bio'
          label='Bio'
          value={bio}
          characterCount={350}
          helpText={bioError ? bioError : ''}
          isInvalid={!!bioError}
          placeholder='Write something about yourself'
          onChange={handleBioChange}
        />
        <Button
          disabled={isPending || !shouldAllowSave || hasError}
          onClick={handleProfileSubmit}
          isFullWidth
        >
          Save Changes
        </Button>
      </EditProfileContainerStyled>
    )
  }

  return <EditAvatar onEditAvatarClick={onEditAvatarClick} />
}

// ================
// Local functions
// ================

const validateInputs = (username: string, bio: string) => {
  const errors: ValidationErrors = {}
  let valid = true

  if (username.trim().length < 2) {
    errors.username = 'Profile name must contain at least 2 characters'
    valid = false
  } else if (/[^a-zA-Z0-9\s.,'"-]/.test(username)) {
    errors.username = 'Profile name cannot contain special characters'
    valid = false
  }

  if (/[^a-zA-Z0-9\s.,'"-]/.test(bio)) {
    errors.bio = 'Bio cannot contain special characters'
    valid = false
  }

  return { valid, errors }
}

function isError(item: object): item is ApiServiceError {
  return 'response' in item
}

// ======
// Styles
// ======

const EditProfileContainerStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.big,

  '> button:first-of-type': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    padding: 0,
    background: 0,
    border: 0,
    margin: 'auto',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    transition: 'opacity 0.25s ease-out',

    ':hover': { opacity: 0.75 },
    ':active': { opacity: 0.5 },

    '> span': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      bottom: 0,
      right: 5,
      width: spacing.bigx3,
      height: spacing.bigx3,
      borderRadius: radius.full,
      border: `0.5px solid ${colors.neutral[200]}`,
      backgroundColor: colors.white,
      boxShadow: shadow.xs,
    },
  },
})

// ======
// Types
// ======

type ValidationErrors = {
  username?: string
  bio?: string
}
